import type {
  ArSavedReportFilterConfig,
  GetArAgingInput,
  GetArCustomAgingInput,
} from '@erp/rpc';
import type { ArAgingBasis } from '@erp/shared-types';
import type { ArReportSettingsValue } from './ArReportSettings';

/**
 * Canonical shape of the AR report filter settings — the basis + day range
 * cluster that `ArReportSettings` edits. All consumers (custom aging report,
 * AR summary, bulk send dialog, saved reports) hold a single value of this
 * shape and route it through the payload builders below to talk to the
 * backend.
 *
 * To add a new filter field (e.g. an "exclude credit memos" toggle):
 *   1. Add it to `ArReportSettingsValue` in `ArReportSettings.tsx`
 *   2. Render the control in `ArReportSettings`'s JSX
 *   3. Add it to `DEFAULT_AR_REPORT_FILTERS` here
 *   4. Forward it from the relevant payload builder below
 *   5. (If it should persist across saved reports) add it to
 *      `ArSavedReportFilterConfigSchema` in the rpc contract and to the
 *      saved-report mapping helpers below.
 *
 * No need to touch each consuming page — they read/write `settings` as a
 * single object.
 */
export type ArReportFilters = ArReportSettingsValue;

/** Initial value for fresh report state. */
export const DEFAULT_AR_REPORT_FILTERS: ArReportFilters = {
  basis: 'days_overdue',
  minDays: 0,
  maxDays: undefined,
  currentOnly: false,
};

/**
 * Org-level lock on the aging basis. When `lockBasis` is true the basis
 * selector is hidden on every statement / report surface and `lockedBasis` is
 * forced into the filters. Sourced from the AR module config via
 * `useArStatementBasisLock`.
 */
export interface ArBasisLock {
  lockBasis: boolean;
  lockedBasis: ArAgingBasis | null;
}

/**
 * Force the org-locked basis onto a filter cluster. Clears `currentOnly` for
 * non-due-date bases (mirroring `ArReportSettings`'s own rule, since a future
 * ship/invoice date rarely matches). Returns the input unchanged — same
 * reference — when no lock applies, so it is safe in memo dependency arrays.
 */
export function applyBasisLock(
  filters: ArReportFilters,
  lock: ArBasisLock,
): ArReportFilters {
  if (!lock.lockBasis || lock.lockedBasis == null) return filters;
  const mustClearCurrentOnly =
    lock.lockedBasis !== 'days_overdue' && filters.currentOnly === true;
  if (filters.basis === lock.lockedBasis && !mustClearCurrentOnly) {
    return filters;
  }
  const next: ArReportFilters = { ...filters, basis: lock.lockedBasis };
  if (lock.lockedBasis !== 'days_overdue') next.currentOnly = false;
  return next;
}

/**
 * Quick-action presets shown above the filter inputs. Each preset describes
 * a partial filter override; `applyPreset` merges it onto the current state
 * so unrelated fields (basis, etc.) are preserved.
 */
export interface Preset {
  label: string;
  filters: Partial<ArReportFilters>;
}

export const PRESETS: Preset[] = [
  // "Current" uses the dedicated currentOnly flag (refDate >= today). The old
  // minDays=0,maxDays=0 encoding silently returns nothing under the new
  // semantics where minDays=0 means "no minimum age."
  //
  // Forces basis back to days_overdue: currentOnly only makes sense on the
  // due-date basis (a future ship/invoice date is rarely meaningful), and
  // applyPreset merges, so without this an existing ship/invoice-date basis
  // would carry over and produce an empty result.
  {
    label: 'Current',
    filters: {
      basis: 'days_overdue',
      currentOnly: true,
      minDays: 0,
      maxDays: undefined,
    },
  },
  {
    label: '1-30',
    filters: { currentOnly: false, minDays: 1, maxDays: 30 },
  },
  {
    label: '31-60',
    filters: { currentOnly: false, minDays: 31, maxDays: 60 },
  },
  {
    label: '61-90',
    filters: { currentOnly: false, minDays: 61, maxDays: 90 },
  },
  {
    label: '90+',
    filters: { currentOnly: false, minDays: 91, maxDays: undefined },
  },
];

export function applyPreset(
  filters: ArReportFilters,
  preset: Preset,
): ArReportFilters {
  return { ...filters, ...preset.filters };
}

/** Match a filter state to a preset by comparing all preset-controlled fields. */
export function findMatchingPreset(
  filters: ArReportFilters,
): Preset | undefined {
  return PRESETS.find((p) =>
    Object.entries(p.filters).every(
      ([key, value]) => filters[key as keyof ArReportFilters] === value,
    ),
  );
}

/**
 * Subset of `GetArAgingInput` controlled by the filter settings. minDays=0
 * is treated as "no minimum age" and omitted so the backend skips the
 * range filter entirely. Spread into the rest of the RPC payload by the
 * caller.
 */
export function arAgingPayload(
  f: ArReportFilters,
): Pick<GetArAgingInput, 'basis' | 'minDays' | 'maxDays' | 'currentOnly'> {
  return {
    basis: f.basis,
    ...(f.minDays > 0 && { minDays: f.minDays }),
    ...(f.maxDays !== undefined && { maxDays: f.maxDays }),
    ...(f.currentOnly === true && { currentOnly: true }),
  };
}

/**
 * Subset of `GetArCustomAgingInput` controlled by the filter settings.
 * minDays is always included because the custom-aging contract requires it
 * (0 is now a valid "no minimum age" value, not a sentinel).
 */
export function arCustomAgingPayload(
  f: ArReportFilters,
): Pick<
  GetArCustomAgingInput,
  'basis' | 'minDays' | 'maxDays' | 'currentOnly'
> {
  return {
    basis: f.basis,
    minDays: f.minDays,
    ...(f.maxDays !== undefined && { maxDays: f.maxDays }),
    ...(f.currentOnly === true && { currentOnly: true }),
  };
}

/**
 * Translate live filter settings into the persisted saved-report shape.
 * The contract schema for saved reports owns its own copy of these fields
 * (it also carries customerIds/status/viewMode), so we deliberately project
 * down to just the filter-cluster fields here and let the caller merge the
 * non-filter fields.
 */
export function settingsToSavedFilterConfig(
  f: ArReportFilters,
): Pick<
  ArSavedReportFilterConfig,
  'basis' | 'minDays' | 'maxDays' | 'currentOnly'
> {
  return {
    basis: f.basis,
    minDays: f.minDays,
    ...(f.maxDays !== undefined && { maxDays: f.maxDays }),
    ...(f.currentOnly === true && { currentOnly: true }),
  };
}

/** Translate a persisted saved-report config back into live filter settings. */
export function savedFilterConfigToSettings(
  fc: Pick<
    ArSavedReportFilterConfig,
    'basis' | 'minDays' | 'maxDays' | 'currentOnly'
  >,
): ArReportFilters {
  return {
    basis: fc.basis,
    minDays: fc.minDays,
    maxDays: fc.maxDays,
    currentOnly: fc.currentOnly === true,
  };
}
